import { paramCase } from 'change-case';
import flatMap from 'lodash/flatMap';
import { isObject } from '../../common/utils/type';

/**
 * Returns a wrapper array of given val if it is not an array
 * @param {*} val
 * @returns {Array} Source array or wrapper array
 */
const arrify = (val) => (Array.isArray(val) ? val : [val]);

/**
 * Returns an array of class names that passes their condition
 * @param {Object} obj { classname: boolean } object
 * @returns {Array.<string>} Array of class names which has true as value in given object
 */
const classListFromConditionalObject = (obj) =>
  Object.keys(obj).reduce((result, classname) => {
    const condition = obj[classname]; // value is a true or false condition
    if (condition) {
      result.push(classname);
    }
    return result;
  }, []);

const classListIterator = (iteratorFn) => (classnames) => {
  if (isObject(classnames)) {
    return classListFromConditionalObject(classnames).map(iteratorFn);
  } if (Array.isArray(classnames)) {
    return classnames.map(iteratorFn);
  } // string
  return iteratorFn(classnames);
};

/**
 * Returns a function to build a BEM element class
 * @param {string} block Block name
 * @returns {Function} Curried function that accepts an string and returns a bem element class
 */
const bemElement = (block) => (element) => `${block}__${element}`;

/**
 * Builds an array of modifier classes for a BEM block or element
 * @param {string} baseClass - base block or element
 * @param {string|string[]|Object.<string, boolean>} modClasses - modifiers to apply
 * @returns {string[]} - modifier classes for given base class
 */
const bemModifiers = (baseClass, modifiers = []) =>
  flatMap(arrify(modifiers), classListIterator((mod) => `${baseClass}--${mod}`));

/**
 * Builds an array of classes which contains:
 * - The main class for a BEM block or element
 * - The modifier classes for that BEM block or element
 * @param {string} bemClass - base block or element
 * @param {string|string[]|Object.<string, boolean>} modifiers - modifiers to apply
 * @returns {string[]} - modifier classes for given base class
 */
const bemClassAndModifiers = (bemClass, modifiers) => [
  bemClass,
  ...bemModifiers(bemClass, modifiers),
];

export default {
  props: {
    /**
     * Optional prop to add extra block names to a component. It provides the way
     * of customizing the look&feel of elements of child components by means of
     * creating styles extensions.
     *
     * For instance:
     * <TheMenuItem />
     * <TheMenuItem bem="submenu-item"/>
     */
    bem: {
      type: [Array, String],
      default: () => [],
    },
  },
  computed: {
    /**
     * @return {string[]} Array of block classes (component name plus "bem" prop)
     */
    bemBlocknames() {
      return [
        ...arrify(this.bem),
        paramCase(this.$options.name),
      ];
    },
    /**
     * @return {string[]} Array of block classes (component name plus "bem" prop)
     */
    bemBlock() {
      return this.bemBlocknames;
    },
  },
  methods: {
    /**
     * Builds an array of classes which contains:
     * - The class (or classes) of the block
     * - The modifier classes for that element
     *
     * Examples for: <TheSample bem="alt-block" />
     *
     *  1. bemBlockMod()
     *      => ['the-sample', 'alt-block']
     *  2. bemBlockMod('closed')
     *      => ['the-sample', 'the-sample--closed', 'alt-block', 'alt-block--closed']
     *  3. bemBlockMod(['closed', 'fixed'])
     *      => ['the-sample', 'the-sample--closed', 'the-sample--fixed', 'alt-block', 'alt-block--closed', 'alt-block--fixed']
     *  4. bemBlockMod({
     *      closed: false,
     *      'bottom-fixed': true  // use quotes when using hyphens
     *     })
     *      => ['the-sample', 'the-sample--bottom-fixed', 'alt-block', 'alt-block--bottom-fixed']
     *
     * @param {string|string[]|Object.<string, boolean>} modifiers - modifiers to apply
     * @returns {string[]} - resultant blocks and modifier classes
     */
    bemBlockMod(modifiers = []) {
      return flatMap(
        this.bemBlock,
        (block) => bemClassAndModifiers(block, modifiers),
      );
    },
    /**
     * Builds an array of classes which contains:
     * - The class (or classes) for the element
     * - The modifier classes for that element
     *
     * Examples for: <TheSample />
     *
     *  1. bemElement('list')
     *      => ['the-sample__list']
     *  2. bemElement('list', 'closed')
     *      => ['the-sample__list', 'the-sample__list--closed']
     *  3. bemElement('list', ['closed', 'fixed'])
     *      => ['the-sample__list', 'the-sample__list--closed', 'the-sample__list--fixed']
     *  4. bemElement({
     *      closed: false,
     *      fixed: true
     *     })
     *      => ['the-sample__list', 'the-sample__list--bottom-fixed']
     *
     * @param {string} elementName - the name of the element
     * @param {string|string[]|Object.<string, boolean>} modifiers - modifiers to apply
     * @returns {string[]} - resultant element and modifier classes
     */
    bemElement(elementName, modifiers = []) {
      const bemElementClassnames = this.bemBlocknames.map(
        (blockname) => bemElement(blockname)(elementName),
      );
      const modifierArray = arrify(modifiers);
      if (modifierArray.length > 0) {
        return flatMap(
          bemElementClassnames,
          (element) => bemClassAndModifiers(element, modifiers),
        );
      }
      return bemElementClassnames;
    },
  },
};
